import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { FinanceiroService } from '../../../../services/financeiro.service';
import { VendaDTO, VendaCancelamentoSumario } from '../../../../models/financeiro.model';
import { VendaBalcaoCupomModal } from '../../../modal/venda-balcao-cupom-modal/venda-balcao-cupom-modal';
import { ConfirmDialogComponent } from '../../../modal/confirm-dialog/confirm-dialog';
import { LogVendaModalComponent } from '../../../modal/log-venda-modal/log-venda-modal';

@Component({
  selector: 'app-vendas',
  standalone: true,
  imports: [CommonModule, FormsModule, VendaBalcaoCupomModal, ConfirmDialogComponent, LogVendaModalComponent],
  templateUrl: './vendas.component.html',
  styleUrls: ['./vendas.component.css'],
})
export class VendasComponent implements OnInit {
  vendas: VendaDTO[] = [];
  filteredVendas: VendaDTO[] = [];
  searchTerm = '';
  isVendaBalcaoOpen = false;

  isCancelDialogOpen = false;
  vendaParaCancelar: VendaDTO | null = null;
  cancelamentoEmAndamento = false;
  sumarioCancelamento: VendaCancelamentoSumario | null = null;
  carregandoSumario = false;
  readonly cancelMessage = 'Esta venda será cancelada. Todo o movimento financeiro, as contas a receber e o orçamento vinculados também serão cancelados. Deseja continuar?';

  isLogVendaOpen = false;
  vendaSelecionadaLog: VendaDTO | null = null;

  currentPage = 0;
  itemsPerPage = 10;
  totalPages = 1;
  totalElements = 0;

  maxPagesToShow = 5;
  startPage = 0;
  endPage = 0;
  pages: number[] = [];

  private cdr = inject(ChangeDetectorRef);

  constructor(
    private financeiroService: FinanceiroService,
    private router: Router,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadVendas();
  }

  loadVendas(): void {
    this.financeiroService.getVendas(this.currentPage, this.itemsPerPage).subscribe({
      next: (data) => {
        this.vendas = data.content;
        this.filteredVendas = data.content;
        this.totalPages = data.totalPages;
        this.totalElements = data.totalElements;
        this.updatePageNumbers();
        this.cdr.detectChanges();
      },
      error: (error) => {
        const mensagem = error?.error?.message ?? 'Erro ao carregar vendas.';
        this.toastr.error(mensagem);
      }
    });
  }

  setPage(page: number): void {
    if (page < 0 || page >= this.totalPages) {
      return;
    }

    this.currentPage = page;
    this.loadVendas();
  }

  updatePageNumbers(): void {
    const halfPages = Math.floor(this.maxPagesToShow / 2);
    this.startPage = Math.max(0, this.currentPage - halfPages);
    this.endPage = Math.min(this.totalPages - 1, this.startPage + this.maxPagesToShow - 1);

    if (this.endPage - this.startPage + 1 < this.maxPagesToShow) {
      this.startPage = Math.max(0, this.endPage - this.maxPagesToShow + 1);
    }

    this.pages = Array.from(
      { length: this.endPage - this.startPage + 1 },
      (_, i) => this.startPage + i
    );
  }

  search(): void {
    const termo = this.searchTerm.trim();
    if (!termo) {
      this.limparBusca();
      return;
    }

    const isNumerico = /^\d+$/.test(termo);
    const id = isNumerico ? Number(termo) : undefined;
    const nomeCliente = isNumerico ? undefined : termo;

    this.financeiroService.buscarVendas(id, nomeCliente).subscribe({
      next: (data) => {
        this.filteredVendas = data ?? [];
        this.totalPages = 1;
        this.totalElements = this.filteredVendas.length;
        this.currentPage = 0;
        this.updatePageNumbers();
        this.cdr.detectChanges();
      },
      error: (error) => {
        const mensagem = error?.error?.message ?? 'Erro ao buscar vendas.';
        this.toastr.error(mensagem);
        this.cdr.detectChanges();
      }
    });
  }

  limparBusca(): void {
    this.searchTerm = '';
    this.currentPage = 0;
    this.loadVendas();
  }


  closeVendaBalcao(vendaConcluida: boolean): void {
    this.isVendaBalcaoOpen = false;
    if (vendaConcluida) {
      this.currentPage = 0;
      this.loadVendas();
    }
    this.cdr.detectChanges();
  }

  visualizarVenda(venda: VendaDTO): void {
    if (!venda.idOrcamento || venda.idOrcamento <= 0) {
      this.toastr.warning('Venda sem orçamento vinculado para visualização.');
      return;
    }

    this.router.navigate(['/home', 'venda-orcamento-item', venda.idOrcamento], {
      queryParams: { origem: 'vendas' }
    });
  }

  abrirCancelarVenda(venda: VendaDTO): void {
    this.vendaParaCancelar = venda;
    this.sumarioCancelamento = null;
    this.carregandoSumario = true;
    this.isCancelDialogOpen = true;
    this.cdr.detectChanges();

    this.financeiroService.getSumarioCancelamentoVenda(venda.id).subscribe({
      next: (sumario) => {
        this.sumarioCancelamento = sumario;
        this.carregandoSumario = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        const mensagem = error?.error?.message ?? 'Erro ao carregar o sumário do cancelamento.';
        this.toastr.error(mensagem);
        this.carregandoSumario = false;
        this.isCancelDialogOpen = false;
        this.vendaParaCancelar = null;
        this.cdr.detectChanges();
      }
    });
  }

  abrirLogVenda(venda: VendaDTO): void {
    this.vendaSelecionadaLog = venda;
    this.isLogVendaOpen = true;
  }

  fecharLogVenda(): void {
    this.isLogVendaOpen = false;
    this.vendaSelecionadaLog = null;
  }

  fecharCancelarVenda(): void {
    if (this.cancelamentoEmAndamento) {
      return;
    }
    this.isCancelDialogOpen = false;
    this.vendaParaCancelar = null;
    this.sumarioCancelamento = null;
    this.carregandoSumario = false;
    this.cdr.detectChanges();
  }

  confirmarCancelamento(): void {
    if (!this.vendaParaCancelar || this.cancelamentoEmAndamento) {
      return;
    }

    this.cancelamentoEmAndamento = true;
    this.cdr.detectChanges();

    this.financeiroService.cancelarVenda(this.vendaParaCancelar.id).subscribe({
      next: () => {
        this.toastr.success('Venda cancelada com sucesso.');
        this.cancelamentoEmAndamento = false;
        this.isCancelDialogOpen = false;
        this.vendaParaCancelar = null;
        this.sumarioCancelamento = null;
        this.loadVendas();
      },
      error: (error) => {
        const mensagem = error?.error?.message ?? 'Erro ao cancelar a venda.';
        this.toastr.error(mensagem);
        this.cancelamentoEmAndamento = false;
        this.isCancelDialogOpen = false;
        this.vendaParaCancelar = null;
        this.sumarioCancelamento = null;
        this.cdr.detectChanges();
      }
    });
  }

}
